import { ref, reactive, } from 'vue'
import { getAuth, onAuthStateChanged, connectAuthEmulator, signOut, sendEmailVerification, signInWithCustomToken, } from "firebase/auth";
import { getFunctions, httpsCallable, connectFunctionsEmulator, } from "firebase/functions";
import { getAnalytics, } from "firebase/analytics";
import { getFirestore, connectFirestoreEmulator, runTransaction,  } from "firebase/firestore";
import { collection, query, where, or, doc, } from "firebase/firestore";
import { FirebaseUtils } from './Common/FirebaseUtils.js';
import * as ser from './Common/SerUtil.js'
import {
  PleaseContactStr,
  IntervalTimer,
  YesNoOrChecking,
  AsyncLock,
  TokenBucket,
} from './Base.js'
import { License } from './Common/License.js'
import { AdminData } from './Common/AdminData.js'
import { makePromiseObj } from './Common/ReqMap.js'
import { SentryWrapper } from './Common/SentryWrapper.js';
import { PsychrometricsCalculator } from './Components/PsychrometricsCalculator.js';
import { SolarCalculator } from './Components/SolarCalculator.js';
import { AffinityLawsCalculator } from './Components/AffinityLawsCalculator.js';
import { HydronicsCalculator } from './Components/HydronicsCalculator.js';
import { DuctulatorCalculator } from './Components/DuctulatorCalculator.js';

import { prettyJson, clearArray, valOr, getElemNames,
  addElem, removeElem, elemIn,
  extendArray, extendMap, isObject,
  runAfterDelay, waitMillis, awaitAll,
  Result, downloadTextFile,
} from './SharedUtils.js'

import { ResidentialProject } from './Components/ResidentialProject.js'
import { CommercialProject } from './BuildingComponents/CommercialProject.js';
import { ProjectTypes } from './Project.js';
export { ResidentialProject } from './Components/ResidentialProject.js'

import { ProjectLock } from './Common/ProjectLock.js'
import { PdfStorage } from './Common/PdfStorage.js'

export { Units } from './Common/Units.js'

export * from './Common/Field.js'

import { gApp, setGlobalApp, DebugOn, IsTestEnv, } from './Globals.js'
export { gApp, DebugOn, } from './Globals.js'

// This is the major.minor.patch version string, e.g., 1.2.3.
// There may be multiple builds with this same version string.
const kAppVersion = __APP_VERSION__

// This is the full version string, with git hash and timestamp
// Use for checking against the "available" version on the server.
const kCurrentAppVersion = import.meta.env.VITE_CURRENT_APP_VERSION;
console.log("CURRENT APP VERSION: " + kCurrentAppVersion);

const kAdminUsers = [
  'mgriley97@gmail.com',
  'madsaurgames@gmail.com',
  'matthew.riley@heatwise-hvac.com',
  'magnus.johnson@heatwise-hvac.com',
  'magnusjohnson49@gmail.com',
];

const kBetaUsers = [
  'mgriley97@gmail.com',
  'madsaurgames@gmail.com',
  'magnus.johnson@heatwise-hvac.com',
  'magnusjohnson49@gmail.com',

  'mike.lund@tweetgarot.com',
  'dan.rehbein@tweetgarot.com',
]

/*
Notes:
For several classes we use the ctor + init pattern because in the ctor, 'this' is not
reactive, which is a problem if we pass them to lambdas.
*/

// const kServerAddr = 'https://'

// We only actually save to backend when a change is detected, so it's fine to do this often
const kAutosaveIntervalSecs = 1;

export class BuilderApp {
  constructor(firebaseApp, router, toaster) {
    this.firebaseApp = firebaseApp;
    this.router = router;
    this.toaster = toaster;
    this.openReplayTracker = null;

    // Initialize PDF storage for temporary PDF data
    this.pdfStorage = new PdfStorage(firebaseApp);

    this.useEmulator = location.hostname == "localhost";
    console.log("Using Firebase emulators: " + this.useEmulator);
    this.db = getFirestore(firebaseApp);
    // Note: I found that I couldn't actually see any data in the firebase emulator UI, so sort of useless.
    /*
    if (this.useEmulator) {
      connectFirestoreEmulator(this.db, "127.0.0.1", 8080);
    }
    */
    this.auth = getAuth(firebaseApp);
    /*
    if (this.useEmulator) {
      connectAuthEmulator(this.auth, "http://127.0.0.1:9099");
    }
    */
    this.analytics = getAnalytics(firebaseApp);
    this.functions = getFunctions(firebaseApp);
    this.functionsEndpoint = 'https://us-central1-buildingcalc.cloudfunctions.net'

    this.serverEndpoint = 'https://heatwise-server-production.up.railway.app';
    if (this.useEmulator) {
      //connectFunctionsEmulator(this.functions, "127.0.0.1", 5001);
      //this.functionsEndpoint = 'http://127.0.0.1:5001/buildingcalc/us-central1'
      this.serverEndpoint = 'http://localhost:3000';
    }

    this.authState = ref("Checking");
    this.authPromiseObj = makePromiseObj()
    this.userInfo = reactive({
      loggedIn: false,
      uid: null,
      email: null,
      emailVerified: false,
      creationTime: null,
    });
    this.license = ref(null);

    this.currentAppVersion = ref(kCurrentAppVersion);
    this.availableAppVersion = ref('');
    this.pageReloadPending = false;
    this.checkForAppUpdateLock = new AsyncLock();
    // Prevent more than 1 auto-update per minute (in case of a bug)
    this.updateTokenBucket = new TokenBucket(1, 1, 'heatwise-update-tokens');

    this.reloadingProjects = ref(false);
    this.projects = reactive([]);
    this.curProj = ref(null);

    // This is used to display a welcome message if they have no projects yet
    this.userHasProjects = ref(YesNoOrChecking.Checking);

    // NOTE - we leave the calculators as null until first use
    this.psychrometricsCalculator = null;
    this.solarCalculator = null;
    this.adminData = AdminData.create();

    this.savingAll = false;
    this.appStartTime = new Date();
    this.autosaveTimer = new IntervalTimer(() => {
      this.doAutosave();
    }, kAutosaveIntervalSecs, {onlyWhenVisible: true});
    this.checkNotifsTimer = new IntervalTimer(() => {
      this.checkUserNotifs();
      this.updateProjLocks();
    }, 3, {onlyWhenVisible: true})
    this.reloadAuthUserLock = new AsyncLock();
    this.loadUserLicenseLock = new AsyncLock();
    this.emailVerificationTimer = new IntervalTimer(() => {
      // While the user's email is not verified, reload auth every 3s to check if it's now verified.
      if (!this.userInfo.emailVerified && this.auth.currentUser) {
          this.reloadAuthUser();
      }
    }, 3, {onlyWhenVisible: true});
    // Reload the user's license every 5 secs, in case it changes
    this.reloadUserLicenseTimer = new IntervalTimer(() => {
      if (this.isLoggedIn()) {
        this.loadUserLicense();
      }
    }, 5, {onlyWhenVisible: true});
    this.autoUpdateTimer = new IntervalTimer(() => {
      this.checkForAppUpdate();
    }, 3, {onlyWhenVisible: true});
  }

  isAdminUser() {
    return elemIn(this.userInfo.email, kAdminUsers)
  }

  isBetaUser() {
    return elemIn(this.userInfo.email, kBetaUsers)
  }

  isLoggedIn() {
    return this.auth.currentUser != null;
  }

  getOpenReplayTracker() {
    return this.openReplayTracker;
  }

  setOpenReplayTracker(tracker) {
    this.openReplayTracker = tracker;
  }

  getAppVersion() {
    return kAppVersion;
  }

  run() {
    onAuthStateChanged(this.auth, (user) => {
      if (user) {
        console.log(`User signed in (id: ${user.uid})`, user);
        this.updateUserInfo(user);
        this.maybeReportSignupConversion(user);
        this.authState.value = "SignedIn";
        SentryWrapper.setUser({
          email: user.email,
          app_version: kAppVersion,
        })
        if (this.openReplayTracker) {
          this.openReplayTracker.setUserID(user.email);
        }
        this.userHasProjects.value = YesNoOrChecking.Checking;
        this.reloadProjects();
        console.log("Resolving auth promise");
        this.authPromiseObj.resolve();
        runAfterDelay(1.0, () => {
          this.checkNotifsTimer.runNow();
        });
        if (!user.emailVerified) {
          this.tryVerifyEmail();
        }
        this.loadUserLicense();
      } else {
        console.log("User signed out");
        this.resetUserInfo();
        this.authState.value = "SignedOut";
        SentryWrapper.setUser(null);
        this.userHasProjects.value = YesNoOrChecking.Checking;
        /*
        // NOTE - we don't navigate like this anymore. Use SignedInView instead.
        // Auto-navigating like this causes problems for routes where you don't actually have
        // to be signed in.
        let currentRouteName = this.router.currentRoute.value.name;
        if (!(currentRouteName == 'home' || currentRouteName == 'signin' || currentRouteName == 'signup')) {
          console.log("Not signed in. Going to home page");
          this.router.replace('/');
        }
        */
        clearArray(this.projects);
      }
    });

    /*
    window.addEventListener("beforeunload", (evt) => {
      evt.preventDefault();
      // For legacy support
      event.returnValue = true;
    });
    */

    // Note: the visibilitychange handler function cannot be async
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState == 'hidden') {
        this.onEnterBackground();
      } else if (document.visibilityState == 'visible') {
        this.onEnterForeground();
      }
    });
    this.onEnterForeground();
  }

  async checkForAppUpdate() {
    await this.checkForAppUpdateLock.run(async () => {
      if (this.pageReloadPending) {
        return;
      }
      // Fetch current-version.txt (no-cache) from the server. Compare with our current version.
      // Note - use '/current-version.txt' instead of 'current-version.txt' because we don't want a relative path.
      let res = await fetch(`/current-version.txt`, {
        cache: 'no-cache',
      });
      if (!res.ok) {
        console.warn(`Error fetching current version: ${res.status} ${res.statusText}`);
        return;
      }
      let availableVersion = await res.text();
      availableVersion = availableVersion.trim();
      this.availableAppVersion.value = availableVersion;

      if (this.availableAppVersion.value != this.currentAppVersion.value) {
        console.log(`New app version available: ${this.currentAppVersion.value} => ${this.availableAppVersion.value}`);

        // Some sanity checks (previous issue with availableAppVersion being HTML of the index page)
        if (this.currentAppVersion.value.length > 100) {
          console.error(`Current app version string is suspiciously long: ${this.currentAppVersion.value}`);
          this.reportError(new Error(`Current app version string is suspiciously long: ${this.currentAppVersion.value}`));
          return;
        }
        if (this.availableAppVersion.value.length > 100) {
          console.error(`Available app version string is suspiciously long: ${this.availableAppVersion.value}`);
          this.reportError(new Error(`Available app version string is suspiciously long: ${this.availableAppVersion.value}`));
          return;
        }
        
        // Check if we can consume a token for this update
        if (this.updateTokenBucket.consume()) {
          gApp.toast("New HeatWise version available. Updating now...");
          this.pageReloadPending = true;
          // Trigger a page reload in 3s...
          setTimeout(() => {
            console.log("Reloading app to update to new version");
            window.location.reload();
          }, 3000);
        } else {
          console.error("Update rate limited - too many updates detected recently");
          this.reportError(new Error(`Update rate limited - too many updates detected recently. ` +
              `Current version: ${this.currentAppVersion.value}, available version: ${this.availableAppVersion.value}`));
        }
      }
    });
  }

  isUpdateAvailable() {
    return this.currentAppVersion.value != this.availableAppVersion.value;
  }

  // Fire a Google Ads conversion the first time we see a newly-created account.
  // This lives here (rather than in the sign-up form) so it covers every signup
  // method - email/password, Microsoft, etc. - from a single place.
  maybeReportSignupConversion(user) {
    try {
      if (this.useEmulator || IsTestEnv()) {
        return;
      }
      // Only report from the real production domain (not staging / preview channels).
      if (location.hostname !== 'app.heatwise-hvac.com') {
        return;
      }
      let meta = user.metadata;
      // Firebase sets creationTime === lastSignInTime only for a brand-new account.
      // lastSignInTime does not change on page reloads or token refreshes, only on
      // an explicit sign-in, so this stays true until the user next signs in.
      if (!meta || !meta.creationTime || meta.creationTime !== meta.lastSignInTime) {
        return;
      }
      // Guard against re-firing while creationTime === lastSignInTime still holds
      // (e.g. the new user reloads the page a few times before signing in again).
      let guardKey = `hw_signup_conversion:${user.uid}`;
      if (localStorage.getItem(guardKey)) {
        return;
      }
      localStorage.setItem(guardKey, '1');
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          send_to: 'AW-18434802551/yIeVCOrcjfAcEPeGs9ZE',
          value: 1.0,
          currency: 'USD',
        });
        console.log('Reported free-trial signup conversion to Google Ads');
      }
    } catch (err) {
      console.error('Error reporting signup conversion:', err);
    }
  }

  updateUserInfo(user) {
    this.userInfo.loggedIn = true;
    this.userInfo.uid = user.uid;
    this.userInfo.email = user.email;
    this.userInfo.emailVerified = user.emailVerified;
    this.userInfo.creationTime = user.metadata.creationTime;
  }

  resetUserInfo() {
    this.userInfo.loggedIn = false;
    this.userInfo.uid = null;
    this.userInfo.email = null;
    this.userInfo.emailVerified = false;
  }

  getUser() {
    return this.userInfo;
  }

  getUserEmail() {
    return this.userInfo.email;
  }
  
  getLicense() {
    return this.license.value;
  }
  
  async loadUserLicense() {
    await this.loadUserLicenseLock.run(async () => {
      // Don't override if current license is a debug override
      if (this.license.value && this.license.value.debugOverride) {
        console.log("Skipping license reload - current license is a debug override");
        return;
      }
      try {
        // Note - we don't require the email to be verified here because we want to load
        // the license (and start the free trial) before the email is verified.
        const userEmail = this.userInfo.email;
        let loadedLicense = await License.loadUserLicense(this, userEmail);
        if (loadedLicense && !loadedLicense.equals(this.license.value)) {
          console.log("Loaded user license: ", loadedLicense);
          this.license.value = loadedLicense;
        }
        // If license doesn't load, keep it as null
        // No default value assignment
      } catch (error) {
        console.error("Error loading user license:", error);
        this.reportError(error);
        this.license.value = null;
      }
    });
  }

  async signOutUser() {
    await this.closeProject();
    await signOut(this.auth);
  }

  async signInWithCustomToken(customToken) {
    try {
      console.log(`Attempting to sign in with custom token`);
      
      // First sign out current user if any
      if (this.auth.currentUser) {
        await this.signOutUser();
      }
      
      // Sign in with the custom token
      const userCredential = await signInWithCustomToken(this.auth, customToken);
      const user = userCredential.user;
      
      console.log(`Successfully signed in with custom token. User: ${user.email}`);
      
      return user;
    } catch (error) {
      console.error('Error signing in with custom token:', error);
      throw new Error(`Failed to sign in with custom token: ${error.message}`);
    }
  }

  async tryVerifyEmail() {
    // Send a verification email if we haven't already
    let fbUtils = new FirebaseUtils(this.userInfo);
    const docRef = doc(this.db, 'userdata', this.userInfo.uid);
    const docSnap = await fbUtils.getDoc(docRef, 'get-userdata');
    if (!docSnap.exists() || !docSnap.data().sentEmailVerif) {
      console.log("Email verification not yet sent. Sending...");
      // Delay to avoid 'auth/too-many-requests'.
      setTimeout(() => {
        this.sendEmailVerification();
      }, 1000);
      if (docSnap.exists()) {
        await fbUtils.updateDoc(docRef, {
          sentEmailVerif: true
        }, 'update-userdata'); 
      } else {
        await fbUtils.setDoc(docRef, {
          sentEmailVerif: true
        }, 'create-userdata'); 
      }
    } else {
      console.log("Already sent an email verif");
    }
  }

  async sendEmailVerification(doToast) {
    console.log("Sending email verification");
    await sendEmailVerification(this.auth.currentUser, {
      // continue-url
      url: 'https://app.heatwise-hvac.com/',
    });
    if (doToast) {
      gApp.toast("Sent!");
    }
    console.log("Sent!");
  }

  getAppTimeSecs() {
    let curTime = new Date();
    return (curTime - this.appStartTime) / 1000.0;
  }

  onEnterForeground() {
    console.log("Entering foreground");
    this.autosaveTimer.start();

    this.checkNotifsTimer.start();
    this.checkNotifsTimer.runNow();

    if (this.isLoggedIn() && !this.userInfo.emailVerified) {
      this.reloadAuthUser();
    }
  }

  async reloadAuthUser() {
    // If our email is unverified, reload and check if it's verified now.
    // Note - onAuthStateChanged will -not- be called when email is verified. We may just
    // get emailVerified set to true in the auth object (but not set in the token that gets
    // sent to firebase APIs & our server).
    await this.reloadAuthUserLock.run(async () => {
      console.log("Reloading auth to check for email verification");
      let prevEmailVerified = this.userInfo.emailVerified;
      await this.auth.currentUser.reload();
      // Reload the ID token (so that if we send to the server it has 'emailVerified' set to true)
      await this.auth.currentUser.getIdToken(true);
      this.updateUserInfo(this.auth.currentUser);
      let curEmailVerified = this.userInfo.emailVerified;
      if (!curEmailVerified) {
        console.log("Email still not verified");
        return;
      }

      if (!prevEmailVerified && curEmailVerified) {
        console.log("Email was verified - reloading projects");
        this.toast("Email verified");

        // Reload a couple things that depend on 'emailVerified' state
        this.reloadProjects();
        runAfterDelay(1.0, () => {
          this.checkNotifsTimer.runNow();
        });
      }
    });
  }

  onEnterBackground() {
    console.log("Entering background");

    // TODO - runNow, too?
    this.autosaveTimer.stop();

    this.checkNotifsTimer.stop();
  }

  doAutosave() {
    let curAppTime = Math.round(this.getAppTimeSecs());
    // console.log(`Autosaving... (${curAppTime}s)`);
    this.saveAll();
  }

  async saveAll() {
    if (this.savingAll) {
      console.log("Already saving all");
      return;
    }
    this.savingAll = true;
    try {
      // Save the current project, if editing
      if (this.proj()) {
        await this.proj().save();
      }
      // Save the psychrometrics calculator
      if (this.psychrometricsCalculator) {
        await this.psychrometricsCalculator.save();
      }
      // Save the affinity laws calculator
      if (this.affinityLawsCalculator) {
        await this.affinityLawsCalculator.save();
      }
      // Save the hydronics calculator
      if (this.hydronicsCalculator) {
        await this.hydronicsCalculator.save();
      }
      // Save the ductulator calculator
      if (this.ductulatorCalculator) {
        await this.ductulatorCalculator.save();
      }
    } finally {
      this.savingAll = false;
    }
  }

  proj() {
    return this.curProj.value;
  }

  async _loadProjectData(projId) {
    /*
    Returns Result<projectData>
    */
    try {
      await this.authPromiseObj.wait();
      console.log(`Loading project data for ID=${projId}`)
      let fbUtils = new FirebaseUtils(this.userInfo);
      const docRef = doc(this.db, 'projects', projId);
      console.log("DocRef: ", docRef);
      const docSnap = await fbUtils.getDoc(docRef);
      if (!docSnap.exists()) {
        console.log("Proj does not exist");
        return Result.error(`Could not find this Project (ID ${projId}). ` +
          `If you think this is an error, please contact support@heatwise-hvac.com.`)
      }
      // console.log("Found project: ", docSnap);
      console.log("Found project: ", docSnap.data());
      return Result.success(docSnap.data());
    } catch (err) {
      console.warn(`Error loading Project (${projId}): ${err.message}`);
      return Result.error(`Error loading Project (with ID ${projId}): ${err.message}. If you think this is an error, please contact `
          + `support@heatwise-hvac.com.`)
    }
  }

  async setCurProj(projId, opts) {
    /*
    Sets the current project to the given projId.
    Be very careful with error handling here. We don't want a situation where
    we fail to completely read the new project, then save back a blank proj (resulting in
    data loss).

    Returns Result<Project>
    */
    opts = valOr(opts, {});

    console.log(`Setting curProj to ${projId}`);
    if (this.curProj.value) {
      if (this.curProj.value.id == projId) {
        console.log(`Already have proj ${projId} open. Returning`);
        return Result.success(this.curProj.value);
      }
      // Have open some other project. Close it.
      await this.closeProject();
      this.curProj.value = null;
    }

    let projDataResult = await this._loadProjectData(projId);
    if (projDataResult.isError()) {
      console.log(`Could not find proj: ${projId}`);
      this.reportError(new Error(`Error loading project ${projId}: ` + projDataResult.getError()));
      return projDataResult;
    }
    console.log(`Found proj: ${projId}. Deserializing.`);

    try {
      let proj = null;
      let projData = projDataResult.getResult();
      if (!projData.type || projData.type == ProjectTypes.Residential) {
        proj = ResidentialProject.create(projId, projData.units);
      } else {
        proj = CommercialProject.create(projId, projData.units);
      }
      // Note: set curProj here so that created objects can reference it.
      // Note: we split Project init into init() and setup() b/c setup() requires
      // gApp.proj() to be set. Messy but fine for now.
      console.log(`Setting gApp.proj() to ${projId}`);
      gApp.curProj.value = proj;
      console.log("Running proj.setup()");
      proj.setup();
      console.log("Deserializing proj data");
      // Note: deserialization may fail if data is malformed. Make sure exceptions
      // are handled here so that we don't end up with a blank project that gets
      // saved (overwriting the original data).
      ser.readFromJson(proj, projData);
      console.log("Done deserializing proj data");
      console.log("Running proj.onPostReadFromJson()");
      await proj.onPostReadFromJson();

      // No need to wait
      proj.updateLastOpenedTime();

      if (proj.isNew) {
        await proj.setupNewProject();
      }

      // Try to acquire the write lock. Fallback to read-only mode
      // TODO - this really should be a lock transaction, but whatever
      let lockOwner = await ProjectLock.getLockOwner(projId);
      if (!lockOwner) {
        await ProjectLock.acquireLock(projId);
        proj.lockedBy = this.userInfo.email;
      }

      // Project is now done loading and ready for use
      proj.setDoneLoading();

      return Result.success(proj);
    } catch (err) {
      console.error(`Error setting curProj to ${projId}: ${err.message}`);
      this.reportError(new Error(`Error setting curProj to ${projId}: ${err.message}`));
      gApp.curProj.value = null;
      return Result.error(`Error setting project to ${projId}: ${err.message}`);
    }
  }

  async canOpenProj(projId) {
    return await ProjectLock.canOpen(projId);
  }

  async stealProjectLock(projId) {
    await ProjectLock.forceAcquireLock(projId);
  }

  async createProject(name, projType, projUnits) {
    // Add the doc to the firebase now so that firebase creates its uid
    console.log("Uid: " + this.userInfo.uid);
    let fbUtils = new FirebaseUtils(this.userInfo);
    const projDoc = doc(collection(this.db, 'projects'));
    await fbUtils.setDoc(projDoc, {
      isNew: true,
      userId: this.userInfo.uid,
      userEmail: this.userInfo.email,
      name: name,
      type: projType,
      units: projUnits,
      sharedUsers: [],
      creationTime: new Date().getTime(),
      lastOpenedTime: new Date().getTime(),
    }, 'create-project');
    console.log(`Created new project: id=${projDoc.id}, type=${projDoc.type}, units=${projUnits}`);
    let projResult = await this.setCurProj(projDoc.id);
    if (projResult.isError()) {
      this.toastError(`Error creating project: ${projResult.getError()}`);
      let developerFeedbackMsg = `The user ${this.userInfo.email} encountered an error creating a project: ` +
        `${projResult.getError()}`;
      this.sendDeveloperFeedback('Error creating project!', developerFeedbackMsg);
      return;
    }
    this.router.push({path: projResult.getResult().getBaseRoute()});
  }

  async duplicateProject(projectId) {
    console.log(`Duplicating project: ${projectId}`);
    let fbUtils = new FirebaseUtils(this.userInfo);
    
    // Read the original project
    const originalDocRef = doc(this.db, 'projects', projectId);
    const snap = await fbUtils.getDoc(originalDocRef, 'get-project-for-duplicate');
    if (!snap.exists()) {
      throw new Error(`Project ${projectId} does not exist`);
    }

    const data = snap.data();
    console.log(`Read project ${projectId} for duplication`);

    // Create a copy with a new doc ID
    const copyDocRef = doc(collection(this.db, 'projects'));

    // Write the copy with updated metadata
    let revisedData = {
      ...data,
      userId: this.userInfo.uid,
      userEmail: this.userInfo.email,
      name: `${data.name} (Copy)`,
      sharedUsers: [],
      creationTime: new Date().getTime(),
      lastOpenedTime: new Date().getTime(),
    };
    
    await fbUtils.setDoc(copyDocRef, revisedData, 'create-duplicate-project');
    console.log(`Duplicated project ${projectId} to ${copyDocRef.id}`);
    
    // Reload projects to show the new duplicate
    await this.reloadProjects();
    
    return copyDocRef.id;
  }

  async makeCommercialCopy(projectId) {
    console.log(`Creating commercial copy of project: ${projectId}`);
    let fbUtils = new FirebaseUtils(this.userInfo);
    
    // Read the original project
    const originalDocRef = doc(this.db, 'projects', projectId);
    const snap = await fbUtils.getDoc(originalDocRef, 'get-project-for-commercial-copy');
    if (!snap.exists()) {
      throw new Error(`Project ${projectId} does not exist`);
    }

    const data = snap.data();
    console.log(`Read project ${projectId} for commercial conversion`);

    // Verify it's a residential project
    if (data.type && data.type !== ProjectTypes.Residential) {
      throw new Error(`Project ${projectId} is not a residential project`);
    }

    // Create a copy with a new doc ID
    const copyDocRef = doc(collection(this.db, 'projects'));

    // Note - we leave the commercial guide and the location data
    // blank for now. Just copy the components, that's the important part.
    let copiedData = {
      wallTypes: data.wallTypes,
      roofTypes: data.roofTypes,
      windowTypes: data.windowTypes,
      interiorShadingTypes: data.interiorShadingTypes,
      exteriorShadingTypes: data.exteriorShadingTypes,
      doorTypes: data.doorTypes,
      skylightTypes: data.skylightTypes,
      bufferSpaceTypes: data.bufferSpaceTypes,
    }
    let revisedData = {
      ...copiedData,
      isNew: true,
      userId: this.userInfo.uid,
      userEmail: this.userInfo.email,
      name: `${data.name} (Commercial)`,
      type: ProjectTypes.Commercial,
      sharedUsers: [],
      creationTime: new Date().getTime(),
      lastOpenedTime: new Date().getTime(),
    };
    
    await fbUtils.setDoc(copyDocRef, revisedData, 'create-commercial-copy');
    console.log(`Created commercial copy of project ${projectId} to ${copyDocRef.id}`);
    
    // Reload projects to show the new commercial copy
    await this.reloadProjects();
    
    return copyDocRef.id;
  }

  async deleteCurProj() {
    let proj = this.curProj.value;
    this.router.push({name: 'home'});
    this.curProj.value = null;
    await this.deleteProjectById(proj.id, proj.name);
  }

  async importProjectFromData(data, opts = {}) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw new Error("Invalid project file: expected a JSON object.");
    }
    console.log("Importing project from file data");
    let fbUtils = new FirebaseUtils(this.userInfo);

    // Create a new doc so that firebase assigns a fresh uid
    const newDocRef = doc(collection(this.db, 'projects'));

    // Copy all of the component/project data, but take over ownership and
    // clear the shared users so that only the importer owns the project.
    let revisedData = {
      ...data,
      userId: this.userInfo.uid,
      userEmail: this.userInfo.email,
      name: opts.name || data.name || "Imported Project",
      sharedUsers: [],
      creationTime: new Date().getTime(),
      lastOpenedTime: new Date().getTime(),
    };

    await fbUtils.setDoc(newDocRef, revisedData, 'import-project');
    console.log(`Imported project to ${newDocRef.id}`);

    // Reload projects to show the imported project
    await this.reloadProjects();

    return newDocRef.id;
  }

  async exportProjectById(projectId) {
    let fbUtils = new FirebaseUtils(this.userInfo);
    const snap = await fbUtils.getDoc(doc(this.db, 'projects', projectId), 'get-project-for-export');
    if (!snap.exists()) {
      throw new Error(`Project ${projectId} does not exist`);
    }
    const data = snap.data();
    const dateStr = new Intl.DateTimeFormat('en-CA').format(new Date());
    const fileName = `${data.name}_${dateStr}.hwise`;
    downloadTextFile(prettyJson(data), fileName);
    this.toast("Project exported");
  }

  async deleteProjectById(projectId, projectName) {
    console.log(`Deleting proj: ${projectName}`);
    let fbUtils = new FirebaseUtils(this.userInfo);
    await fbUtils.deleteDoc(doc(this.db, 'projects', projectId), 'delete-project');
    await waitMillis(500);
    this.toast(`"${projectName}" deleted`);
  }

  async closeProject() {
    if (this.curProj.value) {
      let proj = this.curProj.value;
      await proj.close();
      // Set to null after closing, so that if any last effect handlers run
      // we still have gApp.proj() set.
      this.curProj.value = null;
    }
  }

  isReloadingProjects() {
    return this.reloadingProjects.value;
  }

  async reloadProjects() {
    if (this.reloadingProjects.value) {
      return;
    }
    this.reloadingProjects.value = true;
    try {
      //console.log("Reloading projects...");
      await this.authPromiseObj.wait();
      //console.log("Auth promise resolved");
      let queryCondition = null;
      if (this.userInfo.emailVerified) {
        queryCondition = or(
          where("userId", "==", this.userInfo.uid),
          where("sharedUsers", "array-contains", this.userInfo.email));
      } else {
        queryCondition = where("userId", "==", this.userInfo.uid);
      }
      const q = query(collection(this.db, "projects"), queryCondition);
      let fbUtils = new FirebaseUtils(this.userInfo);
      let querySnapshot = null;
      try {
        querySnapshot = await fbUtils.getDocs(q, 'get-projects');
      } catch (err) {
        this.sendDeveloperFeedback('Error loading projects', `Error loading projects for user ${this.userInfo.email}: ${err.message}`);
        throw err;
      }
      let newProjects = []
      querySnapshot.forEach((doc) => {
        let projData = doc.data(); 
        // console.log("ProjData: ", projData);
        let type = projData.type || ProjectTypes.Residential;
        let projInfo = {
          id: doc.id,
          type: type,
          route: `/${type == ProjectTypes.Residential ? 'house' : 'building'}/${doc.id}`,
          name: projData.name || "Untitled Project",
          lastOpenedTime: projData.lastOpenedTime || 0,
          userId: projData.userId,
          userEmail: projData.userEmail,
          isMine: this.userInfo.uid == projData.userId,
          sharedUsers: projData.sharedUsers || [],
        }
        //console.log(`Name: ${projInfo.name}, IsMine: ${projInfo.isMine}`);
        // console.log("ProjInfo: ", prettyJson(projInfo));
        newProjects.push(projInfo);
      })
      // Sort by most recently opened/touched
      newProjects.sort((projA, projB) => {
        return projB.lastOpenedTime - projA.lastOpenedTime;
      });
      this.userHasProjects.value = newProjects.length > 0 ?
                                    YesNoOrChecking.Yes : YesNoOrChecking.No;
      // Set lock data
      awaitAll(newProjects, async (proj) => {
        proj.lockedBy = await ProjectLock.getLockOwner(proj.id);
      })
      clearArray(this.projects);
      extendArray(this.projects, newProjects);
    } catch (err) {
      console.error("Error loading projects: ", err);
      console.error(`User - Email: ${this.userInfo.email}, Verified: ${this.userInfo.emailVerified}`);
      gApp.reportError(err);
    } finally {
      this.reloadingProjects.value = false;
    }
  }

  async updateProjLocks() {
    //console.log("Updating proj locks");
    awaitAll(this.projects, async (proj) => {
      proj.lockedBy = await ProjectLock.getLockOwner(proj.id);
    })
  }

  toast(msg, opts) {
    opts = valOr(opts, {})
    // console.log(`Toast msg: ${msg}`);
    // TODO - replace with Bootstrap's better toast.
    this.toaster.open({message: msg, ...opts})
  }

  toastError(msg) {
    this.toast(msg, {type: 'error', duration: 20});
  }

  async sendDeveloperFeedback(subject, feedbackTxt) {
    console.log("Sending feedback");
    let fullText = `User: ${this.userInfo.email}\n` +
      `Time: ${new Date().toLocaleString()}\n` +
      `App Version: ${kAppVersion}\n` +
      `Feedback:\n${feedbackTxt}`;
    let data = {
      subject: `${subject} (from ${this.userInfo.email})`,
      text: fullText
    };
    await this.fetchFromServer('send-developer-feedback', 'POST', data);
  }

  async sendUserNotif(userEmail, data) {
    console.log("Sending user notif: ", prettyJson(data));
    await this.fetchFromServer('send-user-notif', 'POST', {
      for: userEmail,
      data: data,
    });
  }

  async fetchFromServer(path, method, data, options = {}) {
    //console.log(`Fetching from server: ${path}, data: ${prettyJson(data)}`);
    let useAuth = valOr(options.useAuth, true);
    // If true (default), report failures to Sentry automatically. Pass false for
    // callers that handle errors themselves to avoid flooding Sentry on retries.
    let reportErrors = valOr(options.reportErrors, true);
    if (useAuth && !this.auth.currentUser) {
      throw new Error("Cannot fetch from server - not signed in");
    }
    try {
      let headers = {
        "Content-Type": "application/json"
      };
      if (useAuth) {
        let idToken = await this.auth.currentUser.getIdToken();
        headers["Authorization"] = `Bearer ${idToken}`;
      }
      let bodyData = data ? JSON.stringify(data) : undefined;
      //console.log(`Fetching from server: ${this.serverEndpoint}/${path}, method: ${method}, body:\n${bodyData}`);
      let res = await fetch(`${this.serverEndpoint}/${path}`, {
        method: method || 'POST',
        headers: headers,
        body: bodyData,
      });
      if (!res.ok) {
        let body = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}. Body: ${body}`);
      }

      // Handle different response types
      if (options.responseType === 'blob') {
        return await res.blob();
      } else if (options.responseType === 'arrayBuffer') {
        return await res.arrayBuffer();
      } else if (options.responseType === 'text') {
        return await res.text();
      } else {
        // Guard against the server returning an HTML error page with a 2xx status
        // (e.g. Railway returning its own landing page when the app is unreachable).
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          const preview = (await res.text()).slice(0, 200);
          throw new Error(
            `Server returned non-JSON response (Content-Type: ${contentType}). ` +
            `Body preview: ${preview}`
          );
        }
        return await res.json();
      }
    } catch (err) {
      let errorMsg = `Error on ${method} ${path}: ${err.message}`;
      console.error(errorMsg);
      if (reportErrors) {
        this.reportError(new Error(errorMsg));
      }
      throw new Error(errorMsg);
    }
  }

  async savePdfDataToStorage(filename, dataString, metadata) {
    return this.pdfStorage.savePdfDataToStorage(filename, dataString, metadata);
  }

  async deletePdfDataFromStorage(filename) {
    return this.pdfStorage.deletePdfDataFromStorage(filename);
  }

  async getPdfDataFromStorage(filename) {
    return this.pdfStorage.getPdfDataFromStorage(filename);
  }

  async getUserNotifs() {
    if (!this.userInfo.emailVerified) {
      return [];
    }

    try {
      // Fetch from functions endpoint:
      // reportErrors: false — called on a 3-second timer; errors are handled by
      // returning [] so auto-reporting would flood Sentry on retry.
      let res = await this.fetchFromServer('get-user-notifs', 'GET', null, { reportErrors: false });
      if (res.error) {
        throw new Error(res.error);
      }
      return res.notifs;
    } catch (err) {
      console.error(`Error getting user notifs: ${err}`);
      return [];
    }
  }

  async clearUserNotifs() {
    if (!this.userInfo.emailVerified) {
      return;
    }
    try {
      // reportErrors: false — called on a 3-second timer; errors are handled silently
      // so auto-reporting would flood Sentry on retry.
      let res = await this.fetchFromServer('clear-user-notifs', 'POST', null, { reportErrors: false });
      if (res.error) {
        throw new Error(res.error);
      }
    } catch (err) {
      console.error(`Error clearing user notifs: ${err}`);
    }
  }

  async checkUserNotifs() {
    if (!this.userInfo.emailVerified) {
      return;
    }
    let notifs = await this.getUserNotifs();
    for (const notif of notifs) {
      console.log(`Got user notif: ${notif.type}`);
      if (notif.type === 'AddedToProject') {
        this.toast(`${notif.owner} added you to project ${notif.name}`, {duration: 10000});
        this.reloadProjects();
      } else if (notif.type === 'RemovedFromProject') {
        this.toast(`${notif.owner} removed you from project ${notif.name}`);
        this.reloadProjects();
      } else {
        throw new Error(`Unexpected notif type: ${notif.type}`, {duration: 10000});
      }
    }
    await this.clearUserNotifs();
  }

  reportError(err, context) {
    SentryWrapper.captureException(err, context);
  }

  onGlobalError(err, instance, info) {
    console.error("Detected global error! ", err);
    const componentName = instance?.$options?.__name
      || instance?.$options?.name
      || 'unknown';
    this.reportError(err, {
      vueInfo: info,
      componentName,
      projId: this.proj()?.id,
      projType: this.proj()?.constructor?.name,
      route: window.location.pathname,
    });
    let msg = `An internal error occurred. ${PleaseContactStr}\nDetails: ${err.message}`
    this.toastError(msg);
  }

  getPsychrometricsCalculator() {
    if (!this.psychrometricsCalculator) {
      this.psychrometricsCalculator = PsychrometricsCalculator.create();
    }
    return this.psychrometricsCalculator;
  }

  getSolarCalculator() {
    if (!this.solarCalculator) {
      this.solarCalculator = SolarCalculator.create();
    }
    return this.solarCalculator;
  }

  getAffinityLawsCalculator() {
    if (!this.affinityLawsCalculator) {
      this.affinityLawsCalculator = AffinityLawsCalculator.create();
    }
    return this.affinityLawsCalculator;
  }

  getHydronicsCalculator() {
    if (!this.hydronicsCalculator) {
      this.hydronicsCalculator = HydronicsCalculator.create();
    }
    return this.hydronicsCalculator;
  }

  getDuctulatorCalculator() {
    if (!this.ductulatorCalculator) {
      this.ductulatorCalculator = DuctulatorCalculator.create();
    }
    return this.ductulatorCalculator;
  }
};

export function initGlobalApp(firebaseApp, router, toaster) {
  setGlobalApp(new BuilderApp(firebaseApp, router, toaster));
  return gApp;
}

export function initGlobalMockApp(mockApp) {
  setGlobalApp(mockApp);
  return gApp;
}

